import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { pluginApi, serverApi } from '@/lib/api';
import { aiInterviewApi, WhitelistFlowForm } from '@/lib/aiInterviewApi';
import { Loader2, Save, Brain, HelpCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

// Crea un flow vuoto (senza id, sarà generato dal backend)
const createEmptyFlow = (): Partial<WhitelistFlowForm> => ({
  title: '',
  rules: '',
  requiredQuestions: 3,
  passScore: 60,
  whitelistRoleId: '',
  staffNotificationChannel: '',
  publishChannelId: '',
  maxAttemptsPerQuestion: 3,
  requireCodeVerification: true,
  enabled: true,
});

function TestWhitelistButton({ serverId, flowId }: { serverId: string, flowId: string }) {
  const [userId, setUserId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const handleTest = async () => {
    if (!userId) {
      toast.error('Inserisci un userId Discord valido');
      return;
    }
    setLoading(true);
    try {
      await aiInterviewApi.testWhitelistFlow(serverId, flowId, userId);
      toast.success('Test flusso avviato! Controlla i log e Discord.');
    } catch (err: any) {
      toast.error('Errore test flusso: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="flex gap-1 items-center">
      <Input
        type="text"
        placeholder="UserId Discord"
        value={userId}
        onChange={e => setUserId(e.target.value)}
        style={{ width: 120, fontSize: 12, padding: '2px 6px' }}
        disabled={loading}
      />
      <Button size="sm" variant="secondary" onClick={handleTest} disabled={loading || !userId} className="gap-1">
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Testa flusso whitelist'}
      </Button>
    </div>
  );
}

export function AIInterviewPlugin({ serverId }: { serverId: string }) {
  const UI_DEBUG_VERSION = 'ai-interview-ui-2026-03-15-flows-sync-1';
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
  const [embedStatus, setEmbedStatus] = useState<Record<string, any>>({});
  const [embedLoading, setEmbedLoading] = useState<Record<string, boolean>>({});
  const [localFlows, setLocalFlows] = useState<WhitelistFlowForm[]>([]);
  const flowsInitializedRef = useRef(false);
  const flowIdsKey = localFlows.map((f) => f.id).join(',');
  // Normalizza snake_case -> camelCase
  function normalizeFlow(f: any): WhitelistFlowForm {
    return {
      id: f.id,
      title: f.title,
      rules: f.rules,
      requiredQuestions: f.required_questions ?? f.requiredQuestions ?? 3,
      passScore: f.pass_score ?? f.passScore ?? 60,
      whitelistRoleId: f.whitelist_role_id ?? f.whitelistRoleId ?? '',
      staffNotificationChannel: f.staff_notification_channel ?? f.staffNotificationChannel ?? '',
      publishChannelId: f.publish_channel_id ?? f.publishChannelId ?? '',
      maxAttemptsPerQuestion: f.max_attempts_per_question ?? f.maxAttemptsPerQuestion ?? 3,
      requireCodeVerification: (f.require_code_verification ?? f.requireCodeVerification ?? true),
      enabled: f.enabled ?? true,
      // ...aggiungi altri campi se servono
    };
  }
  const {
    data: flowsRaw = [],
    refetch: refetchFlows,
    isLoading: flowsLoading,
    isError: flowsIsError,
    error: flowsError,
  } = useQuery<any[]>({
    queryKey: ['servers', serverId, 'ai-interview-flows'],
    enabled: Boolean(serverId),
    queryFn: async () => {
      // eslint-disable-next-line no-console
      console.log('[AIInterviewPlugin] Fetch flows:', serverId);
      return aiInterviewApi.getFlows(serverId);
    },
    onSuccess: (data) => {
      // eslint-disable-next-line no-console
      console.log('[AIInterviewPlugin] getFlows ok:', Array.isArray(data) ? `len=${data.length}` : typeof data);
    },
    onError: (err: any) => {
      // eslint-disable-next-line no-console
      console.error('[AIInterviewPlugin] Failed to load flows:', err);
      toast.error(`Errore caricamento flow: ${err?.message || 'unknown'}`);
    },
    // Avoid background refetches that can overwrite local typing state.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  // Keep localFlows in sync on first load (or after server change) without overwriting user typing edits.
  useEffect(() => {
    if (!serverId) return;
    if (flowsLoading || flowsIsError) return;
    if (!Array.isArray(flowsRaw)) return;

    // Initialize once, but allow recovery if we previously initialized with an empty array and later data arrives.
    if (!flowsInitializedRef.current || (localFlows.length === 0 && flowsRaw.length > 0)) {
      setLocalFlows(flowsRaw.map(normalizeFlow));
      flowsInitializedRef.current = true;
    }
  }, [serverId, flowsLoading, flowsIsError, flowsRaw, localFlows.length]);

  // Reset local flows when switching serverId
  useEffect(() => {
    flowsInitializedRef.current = false;
    setLocalFlows([]);
    setSelectedFlowId(null);
  }, [serverId]);

  const { data: channels = [], isLoading: channelsLoading } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });
  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['servers', serverId, 'roles'],
    queryFn: () => serverApi.getRoles(serverId),
  });

  // Debug log per ruoli/canali (solo quando cambiano davvero, non a ogni render)
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[AIInterviewPlugin] UI version:', UI_DEBUG_VERSION);
    // eslint-disable-next-line no-console
    console.log('[DEBUG] Esempio primo ruolo:', (roles as any[])?.[0]);
    // eslint-disable-next-line no-console
    console.log('[DEBUG] Esempio primo canale:', (channels as any[])?.[0]);
  }, [(roles as any[])?.[0]?.id, (channels as any[])?.[0]?.id]);

  // Seleziona il primo flow disponibile all'avvio o dopo ogni refetch
  useEffect(() => {
    if (localFlows.length === 0) {
      setSelectedFlowId(null);
      return;
    }

    // Mantieni la selezione corrente se il flow esiste ancora.
    setSelectedFlowId((prev) => {
      if (prev && localFlows.some((f) => f.id === prev)) return prev;
      return localFlows[0].id;
    });
  }, [flowIdsKey]);

  // Carica status embed per ogni flow
  useEffect(() => {
    localFlows
      .filter((flow) => flow.id && flow.title && flow.rules)
      .forEach((flow) => {
        setEmbedLoading((prev) => ({ ...prev, [flow.id]: true }));
        aiInterviewApi.getRulesEmbedStatus(serverId, flow.id)
          .then((status) => setEmbedStatus((prev) => ({ ...prev, [flow.id]: status })))
          .finally(() => setEmbedLoading((prev) => ({ ...prev, [flow.id]: false })));
      });
  }, [serverId, localFlows.map((f) => f.id).join(",")]);

  // CRUD flow
  // Utility per convertire camelCase in snake_case
  function toSnakeCase(obj: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = {};
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const snake = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      out[snake] = obj[key];
    }
    return out;
  }

  const handleAddFlow = async () => {
    try {
      // Garantisce requiredQuestions >= 1
      const flow = createEmptyFlow();
      const requiredQuestions = Math.max(1, Number(flow.requiredQuestions) || 3);
      const payload = { ...flow, requiredQuestions };
      const snakePayload = toSnakeCase(payload);
      // Debug: mostra il payload convertito
      console.log("Invio payload:", snakePayload);
      const created = await aiInterviewApi.createFlow(serverId, snakePayload);

      // Mostra subito l'area di configurazione: il backend ritorna solo {id}
      const newId = String((created as any)?.id || '').trim();
      if (newId) {
        const optimistic: WhitelistFlowForm = {
          id: newId,
          title: '',
          rules: '',
          requiredQuestions: requiredQuestions,
          passScore: 60,
          whitelistRoleId: '',
          staffNotificationChannel: '',
          publishChannelId: '',
          maxAttemptsPerQuestion: 3,
          enabled: true,
        };
        setLocalFlows((prev) => (prev.some((f) => f.id === newId) ? prev : [...prev, optimistic]));
        setSelectedFlowId(newId);
      }

      // Poi riallinea con il DB
      void refetchFlows();
      toast.success('Nuovo flow creato!');
    } catch (err) {
      toast.error('Errore creazione flow');
    }
  };

  const handleUpdateFlow = async (
    flowId: string,
    updates: Partial<WhitelistFlowForm>,
    opts?: { refetch?: boolean; silent?: boolean }
  ) => {
    try {
      const currentFlow = localFlows.find((f) => f.id === flowId);
      if (!currentFlow) {
        console.warn("Flow non trovato per update");
        return;
      }

      // Optimistic UI: aggiorna subito localFlows così i Select non tornano a "none"
      const nextFlow: WhitelistFlowForm = {
        ...currentFlow,
        ...updates,
        requiredQuestions: Math.max(1, Number(updates.requiredQuestions ?? currentFlow.requiredQuestions ?? 3) || 3),
        passScore: Number(updates.passScore ?? currentFlow.passScore ?? 60) || 60,
        maxAttemptsPerQuestion: Number(updates.maxAttemptsPerQuestion ?? currentFlow.maxAttemptsPerQuestion ?? 3) || 3,
        enabled: updates.enabled ?? currentFlow.enabled ?? true,
      };
      setLocalFlows((prev) => prev.map((f) => (f.id === flowId ? nextFlow : f)));

      await persistFlowNow(flowId, nextFlow, { refetch: opts?.refetch, silent: opts?.silent });
    } catch (err) {
      console.error("[handleUpdateFlow] ERRORE COMPLETO:", err);
      const msg = err?.response?.data?.error || err?.message || "Errore sconosciuto durante update";
      toast.error(`Salvataggio fallito: ${msg}`);
      await refetchFlows();
    }
  };

  const persistFlowNow = async (flowId: string, flow: WhitelistFlowForm, opts?: { refetch?: boolean; silent?: boolean }) => {
    const shouldRefetch = opts?.refetch ?? true;
    const silent = opts?.silent ?? false;

    const payload = {
      title: flow.title ?? '',
      rules: flow.rules ?? '',
      required_questions: Math.max(1, Number(flow.requiredQuestions ?? 3) || 3),
      pass_score: Number(flow.passScore ?? 60) || 60,
      whitelist_role_id: flow.whitelistRoleId ? flow.whitelistRoleId : null,
      staff_notification_channel: flow.staffNotificationChannel ? flow.staffNotificationChannel : null,
      publish_channel_id: flow.publishChannelId ? flow.publishChannelId : null,
      max_attempts_per_question: Number(flow.maxAttemptsPerQuestion ?? 3) || 3,
      require_code_verification: (flow.requireCodeVerification ?? true),
      enabled: Boolean(flow.enabled),
    };

    console.log("[handleUpdateFlow] Payload che mando:", payload);
    const response = await aiInterviewApi.updateFlow(serverId, flowId, payload);
    console.log("[handleUpdateFlow] Risposta backend:", response);
    if (shouldRefetch) await refetchFlows();
    if (!silent) toast.success("Flow aggiornato!");
  };

  const updateLocalFlow = (flowId: string, patch: Partial<WhitelistFlowForm>) => {
    setLocalFlows((prev) => prev.map((f) => (f.id === flowId ? { ...f, ...patch } : f)));
  };

  const handleRemoveFlow = async (flowId: string) => {
    try {
      await aiInterviewApi.deleteFlow(serverId, flowId);
      await refetchFlows();
      toast.success('Flow eliminato!');
      setSelectedFlowId(null);
    } catch (err) {
      toast.error('Errore eliminazione flow');
    }
  };

  // Trova il flow selezionato (usa localFlows per reattività immediata)
  const selectedFlow = localFlows.find((f) => f.id === selectedFlowId) || null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-xl">
            <Brain className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">AI Interview</h3>
            <p className="text-sm text-muted-foreground">
              Sistema intelligente di whitelist con Gemini AI
            </p>
            <p className="text-xs text-muted-foreground">
              UI: {UI_DEBUG_VERSION}
            </p>
          </div>
        </div>
      </div>
      <div className="glass-card p-6 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-green-400" />
          <h4 className="font-semibold">Whitelist multiple per gioco</h4>
        </div>
        <div className="space-y-4">
          {/* Select per scegliere il flow attivo */}
          <div className="flex items-center gap-2 mb-2">
                <span className="text-xs">Seleziona flow:</span>
                <Select value={selectedFlowId || ''} onValueChange={setSelectedFlowId}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Seleziona flow" />
                  </SelectTrigger>
                  <SelectContent>
                    {localFlows.map((flow) => (
                      <SelectItem key={flow.id} value={flow.id}>{flow.title || `(Nuovo flow)`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
           {flowsLoading && (
             <div className="text-xs text-muted-foreground">Caricamento flow...</div>
           )}
           {flowsIsError && (
             <div className="text-xs text-red-500">
               Errore caricamento flow: {(flowsError as any)?.message || 'unknown'}
             </div>
           )}
           {!flowsLoading && !flowsIsError && (
             <div className="text-[11px] text-muted-foreground">
               Flow caricati: {localFlows.length}{selectedFlowId ? ` | selezionato: ${selectedFlowId}` : ''}
             </div>
           )}
           {!flowsLoading && !flowsIsError && localFlows.length === 0 && (
             <div className="text-xs text-yellow-500">
               Nessun flow trovato. Clicca "Aggiungi whitelist per un altro gioco".
             </div>
           )}
          {selectedFlow ? (
            <div key={selectedFlow.id} className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
              {/* Status embed e pulsante spawn per il flow selezionato */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                <div>
                  <span className="text-xs text-muted-foreground">Status embed:</span>{' '}
                  {embedLoading[selectedFlow.id] ? (
                    <span className="text-xs">Caricamento...</span>
                  ) : embedStatus[selectedFlow.id]?.spawned ? (
                    <span className="text-xs text-green-500">✅ Pubblicato</span>
                  ) : (
                    <span className="text-xs text-yellow-500">❌ Non pubblicato</span>
                  )}
                  {embedStatus[selectedFlow.id]?.messageUrl && (
                    <a
                      href={embedStatus[selectedFlow.id].messageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-2 text-xs text-primary underline"
                    >
                      Apri messaggio
                    </a>
                  )}
                </div>
                <div className="flex flex-col md:flex-row gap-2 items-center">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={
                      !selectedFlow.publishChannelId ||
                      !selectedFlow.title ||
                      !selectedFlow.rules
                    }
                    onClick={async () => {
                      await aiInterviewApi.spawnRulesEmbed(serverId, selectedFlow.id);
                      const status = await aiInterviewApi.getRulesEmbedStatus(serverId, selectedFlow.id);
                      setEmbedStatus((prev) => ({ ...prev, [selectedFlow.id]: status }));
                    }}
                    className="gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Spawna embed whitelist
                  </Button>
                  <TestWhitelistButton serverId={serverId} flowId={selectedFlow.id} />
                </div>
              </div>
              {/* Dettagli del flow selezionato */}
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h5 className="font-medium">Whitelist</h5>
                  <p className="text-xs text-muted-foreground">Un gioco, un regolamento, un ruolo whitelist dedicato.</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Codice DM</span>
                    <Switch
                      checked={selectedFlow.requireCodeVerification}
                      onCheckedChange={(checked) => handleUpdateFlow(selectedFlow.id, { requireCodeVerification: checked })}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Attiva</span>
                    <Switch
                      checked={selectedFlow.enabled}
                      onCheckedChange={(checked) => handleUpdateFlow(selectedFlow.id, { enabled: checked })}
                    />
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => handleRemoveFlow(selectedFlow.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome Gioco</Label>
                  <Input
                    placeholder="Es: GTA RP, Rust, FiveM..."
                    value={selectedFlow.title}
                    onChange={(e) => {
                      const v = e.target.value;
                      updateLocalFlow(selectedFlow.id, { title: v });
                    }}
                  />
                </div>
                <div>
                  <Label>Ruolo Whitelist</Label>
                  {rolesLoading ? (
                    <div className="text-xs text-muted-foreground">Caricamento ruoli...</div>
                  ) : (
                    <Select
                      value={selectedFlow?.whitelistRoleId || 'none'}
                      onValueChange={(value) => {
                        console.log("=== SELEZIONE RUOLO TRIGGERATA ===");
                        console.log("Valore scelto dall'utente:", value);
                        console.log("Flow ID attuale:", selectedFlow?.id);
                        console.log("Valore precedente:", selectedFlow?.whitelistRoleId);
                        const newValue = value === 'none' ? '' : value;
                        handleUpdateFlow(selectedFlow.id, { whitelistRoleId: newValue });
                        setTimeout(() => {
                          const updatedFlow = localFlows.find(f => f.id === selectedFlow.id);
                          console.log("=== DOPO UPDATE (800ms) ===");
                          console.log("whitelistRoleId aggiornato?", updatedFlow?.whitelistRoleId);
                          console.log("selectedFlow aggiornato?", selectedFlow?.whitelistRoleId);
                        }, 800);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona ruolo whitelist" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessun ruolo</SelectItem>
                        {(roles ?? []).map((role: any) => (
                          <SelectItem key={String(role.id)} value={String(role.id)}>
                            {role.name || `Ruolo ${role.id.slice(0,8)}...`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {selectedFlow?.whitelistRoleId && !(roles ?? []).some((r: any) => String(r.id) === String(selectedFlow.whitelistRoleId)) && (
                    <div className="text-xs text-red-500 mt-1">Attenzione: il ruolo selezionato non è più disponibile.</div>
                  )}
                </div>
              </div>
              <div>
                <Label>Regolamento del Gioco</Label>
                <Textarea
                  placeholder="Inserisci il regolamento specifico di questo gioco. L'AI farà la whitelist su queste regole."
                  value={selectedFlow.rules}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateLocalFlow(selectedFlow.id, { rules: v });
                  }}
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Ogni whitelist usa solo il proprio regolamento dedicato.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Numero Domande</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={selectedFlow.requiredQuestions}
                    onChange={(e) => handleUpdateFlow(selectedFlow.id, { requiredQuestions: parseInt(e.target.value || '3', 10) || 3 })}
                  />
                </div>
                <div>
                  <Label>Punteggio Minimo (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={selectedFlow.passScore}
                    onChange={(e) => handleUpdateFlow(selectedFlow.id, { passScore: parseInt(e.target.value || '60', 10) || 60 })}
                  />
                </div>
                <div>
                  <Label>Tentativi per Domanda</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={selectedFlow.maxAttemptsPerQuestion}
                    onChange={(e) => handleUpdateFlow(selectedFlow.id, { maxAttemptsPerQuestion: parseInt(e.target.value || '3', 10) || 3 })}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button
                  type="button"
                  variant="default"
                  className="gap-2"
                  onClick={() => handleUpdateFlow(selectedFlow.id, selectedFlow)}
                >
                  <Save className="w-4 h-4" />
                  Salva configurazione
                </Button>
              </div>
              <div>
                <Label>Canale Notifiche Staff</Label>
                {channelsLoading ? (
                  <div className="text-xs text-muted-foreground">Caricamento canali...</div>
                ) : (
                  <Select
                    value={selectedFlow?.staffNotificationChannel || 'none'}
                    onValueChange={(value) => {
                      console.log("=== SELEZIONE STAFF NOTIF TRIGGERATA ===");
                      console.log("Valore scelto dall'utente:", value);
                      console.log("Flow ID attuale:", selectedFlow?.id);
                      console.log("Valore precedente:", selectedFlow?.staffNotificationChannel);
                      const newValue = value === 'none' ? '' : value;
                      handleUpdateFlow(selectedFlow.id, { staffNotificationChannel: newValue });
                      setTimeout(() => {
                        const updatedFlow = localFlows.find(f => f.id === selectedFlow.id);
                        console.log("=== DOPO UPDATE (800ms) ===");
                        console.log("staffNotificationChannel aggiornato?", updatedFlow?.staffNotificationChannel);
                        console.log("selectedFlow aggiornato?", selectedFlow?.staffNotificationChannel);
                      }, 800);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona canale notifiche staff" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun canale</SelectItem>
                      {(channels ?? []).map((channel: any) => (
                        <SelectItem key={String(channel.id)} value={String(channel.id)}>
                          {channel.name || `Canale ${channel.id.slice(0,8)}...`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedFlow?.staffNotificationChannel && !(channels ?? []).some((c: any) => String(c.id) === String(selectedFlow.staffNotificationChannel)) && (
                  <div className="text-xs text-red-500 mt-1">Attenzione: il canale selezionato non è più disponibile.</div>
                )}
              </div>
              <div>
                <Label>Canale pubblicazione embed whitelist</Label>
                {channelsLoading ? (
                  <div className="text-xs text-muted-foreground">Caricamento canali...</div>
                ) : (
                  <Select
                    value={selectedFlow?.publishChannelId || 'none'}
                    onValueChange={(value) => {
                      console.log("=== SELEZIONE PUBBLICA TRIGGERATA ===");
                      console.log("Valore scelto dall'utente:", value);
                      console.log("Flow ID attuale:", selectedFlow?.id);
                      console.log("Valore precedente:", selectedFlow?.publishChannelId);
                      const newValue = value === 'none' ? '' : value;
                      handleUpdateFlow(selectedFlow.id, { publishChannelId: newValue });
                      setTimeout(() => {
                        const updatedFlow = localFlows.find(f => f.id === selectedFlow.id);
                        console.log("=== DOPO UPDATE (800ms) ===");
                        console.log("publishChannelId aggiornato?", updatedFlow?.publishChannelId);
                        console.log("selectedFlow aggiornato?", selectedFlow?.publishChannelId);
                      }, 800);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona canale dove pubblicare embed/pulsante" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessun canale</SelectItem>
                      {(channels ?? []).map((channel: any) => (
                        <SelectItem key={String(channel.id)} value={String(channel.id)}>
                          {channel.name || `Canale ${channel.id.slice(0,8)}...`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedFlow?.publishChannelId && !(channels ?? []).some((c: any) => String(c.id) === String(selectedFlow.publishChannelId)) && (
                  <div className="text-xs text-red-500 mt-1">Attenzione: il canale selezionato non è più disponibile.</div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Il bot pubblicherà l'embed di avvio whitelist in questo canale.
                </p>
              </div>
            </div>
          ) : null}
          <Button type="button" variant="outline" onClick={handleAddFlow} className="gap-2 w-full">
            <Plus className="w-4 h-4" />
            Aggiungi whitelist per un altro gioco
          </Button>
        </div>
      </div>
      <div className="glass-card p-6 space-y-4">
        <h4 className="font-semibold mb-2">Come funziona</h4>
        <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
          <h5 className="font-medium text-green-400 mb-2">Whitelist AI</h5>
          <ol className="text-sm text-muted-foreground space-y-1 ml-4 list-decimal">
            <li>L'utente usa <code className="text-purple-400">/start-whitelist</code></li>
            <li>Se hai più giochi attivi, il bot mostra un menu per scegliere il gioco corretto</li>
            <li>L'AI genera domande basate solo sul regolamento del gioco selezionato</li>
            <li>Analizza le risposte per capire la comprensione reale</li>
            <li>Fornisce feedback iterativo se la risposta è insufficiente</li>
            <li>Se supera la verifica, invia un codice DM e assegna il ruolo whitelist di quel gioco</li>
          </ol>
        </div>
      </div>
      <div className="glass-card p-6 space-y-4">
        <h4 className="font-semibold mb-2">Comandi disponibili</h4>
        <div className="p-3 bg-white/5 rounded-lg text-sm">
          <code className="text-purple-400">/start-whitelist</code>
          <p className="text-muted-foreground mt-1">
            Avvia il processo di whitelist guidato dall'AI
          </p>
        </div>
      </div>
      <div className="glass-card p-6 space-y-3">
        <h4 className="font-semibold mb-2">Best practices</h4>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            <span>Scrivi un regolamento dettagliato con esempi concreti</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            <span>Monitora le whitelist completate per verificare la qualità dell'AI</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400">✓</span>
            <span>Configura un canale staff per escalation rapida</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-yellow-400">!</span>
            <span>L'AI è un assistente e non sostituisce completamente lo staff</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default AIInterviewPlugin;
